import type { VacationOverviewDocument } from '../../models/vacationOverview.types';
import type { DeadlineRiskRow } from './panoramaDeadlineRisk';

export type RiskLevel = 'expired' | 'expiring' | 'requested' | 'scheduled' | 'safe';

export interface PeopleFilters {
	collaboratorId?: string | null;
	client?: string | null;
	department?: string | null;
	position?: string | null;
	leader?: string | null;
	risk?: RiskLevel | 'at-risk' | null;
}

export interface FilterOptions {
	collaborators: Array<{ id: string; name: string }>;
	clients: Array<{ name: string; count: number }>;
	departments: Array<{ name: string; count: number }>;
	positions: Array<{ name: string; count: number }>;
	leaders: Array<{ name: string; count: number }>;
}

export function filterPeople(
	documents: VacationOverviewDocument[],
	filters: PeopleFilters,
	riskRowsMap: Map<string, DeadlineRiskRow>
): VacationOverviewDocument[] {
	const activeBase = documents.filter(d => d.active && d.inBase);

	return activeBase.filter(doc => {
		if (filters.collaboratorId && doc.collaboratorId !== filters.collaboratorId) {
			return false;
		}

		if (filters.client) {
			const hasClient = doc.clientNames.includes(filters.client);
			if (!hasClient) return false;
		}

		if (filters.department && doc.departmentName !== filters.department) {
			return false;
		}

		if (filters.position && doc.positionName !== filters.position) {
			return false;
		}

		if (filters.leader && doc.responsibleName !== filters.leader) {
			return false;
		}

		if (filters.risk) {
			const riskRow = riskRowsMap.get(doc.collaboratorId);
			if (!riskRow) return false;

			if (filters.risk === 'at-risk') {
				if (!['expired', 'expiring', 'requested'].includes(riskRow.riskLevel)) {
					return false;
				}
			} else {
				if (riskRow.riskLevel !== filters.risk) {
					return false;
				}
			}
		}

		return true;
	});
}

export function buildFilterOptions(documents: VacationOverviewDocument[]): FilterOptions {
	const activeBase = documents.filter(d => d.active && d.inBase);
	const collaborators = new Map<string, string>();
	const clients = new Map<string, number>();
	const departments = new Map<string, number>();
	const positions = new Map<string, number>();
	const leaders = new Map<string, number>();

	activeBase.forEach(doc => {
		collaborators.set(doc.collaboratorId, doc.name);

		doc.clientNames.forEach(client => {
			clients.set(client, (clients.get(client) ?? 0) + 1);
		});

		if (doc.departmentName) {
			departments.set(doc.departmentName, (departments.get(doc.departmentName) ?? 0) + 1);
		}

		if (doc.positionName) {
			positions.set(doc.positionName, (positions.get(doc.positionName) ?? 0) + 1);
		}

		if (doc.responsibleName) {
			leaders.set(doc.responsibleName, (leaders.get(doc.responsibleName) ?? 0) + 1);
		}
	});

	return {
		collaborators: Array.from(collaborators.entries())
			.map(([id, name]) => ({ id, name }))
			.sort((a, b) => a.name.localeCompare(b.name)),
		clients: Array.from(clients.entries())
			.map(([name, count]) => ({ name, count }))
			.sort((a, b) => a.name.localeCompare(b.name)),
		departments: Array.from(departments.entries())
			.map(([name, count]) => ({ name, count }))
			.sort((a, b) => a.name.localeCompare(b.name)),
		positions: Array.from(positions.entries())
			.map(([name, count]) => ({ name, count }))
			.sort((a, b) => a.name.localeCompare(b.name)),
		leaders: Array.from(leaders.entries())
			.map(([name, count]) => ({ name, count }))
			.sort((a, b) => a.name.localeCompare(b.name)),
	};
}
